/**
 * Utility functions - please be judicious with what you put in here as you can typically just put these in the
 * file they are used.
 * @module
 */

export function clampNeg1Pos1(a: number): number {
  return Math.max(Math.min(a, 1), -1);
}

export function hashInts2(a: number, b: number): number {
  // cantor pairing function
  return ((a + b) * (a + b + 1)) / 2 + a;
}

export function hashInts3(a: number, b: number, c: number): number {
  return hashInts2(hashInts2(a, b), c);
}

export function range(n: number): number[] {
  const a: number[] = new Array(n);
  for (let i = 0; i < n; i++) {
    a[i] = i;
  }
  return a;
}

export function clearArray<T>(a: T[]) {
  for (let i = 0, l = a.length; i < l; i++) {
    a.pop();
  }
}

export function rangeBetween(min: number, max: number, step: number = 1) {
  let len = 1 + Math.floor((max - min) / step);

  // account for possible floating point precision issues in division
  if (min + len * step <= max) {
    len++;
  }

  const a: number[] = new Array(len);
  for (let i = 0; i < a.length; i++) {
    a[i] = min + i * step;
  }
  return a;
}

export function pushXY<T, E extends T>(a: T[], x: E, y: E) {
  a.push(x);
  a.push(y);
}

export function pushXYZ<T, E extends T>(a: T[], x: E, y: E, z: E) {
  a.push(x);
  a.push(y);
  a.push(z);
}

export function pushXYXY<T, E extends T>(a: T[], ax: E, ay: E, bx: E, by: E) {
  a.push(ax);
  a.push(ay);
  a.push(bx);
  a.push(by);
}

export function pushXYZXYZ<T, E extends T>(a: T[], ax: E, ay: E, az: E, bx: E, by: E, bz: E) {
  a.push(ax);
  a.push(ay);
  a.push(az);
  a.push(bx);
  a.push(by);
  a.push(bz);
}

export function pushXYZArray<T, E extends T>(a: T[], x: E, y: E, z: E, b: ArrayLike<E>) {
  a.push(x);
  a.push(y);
  a.push(z);
  for (let i = 0, l = b.length; i < l; i++) {
    a.push(b[i]);
  }
}

export function pushArrayXYZ<T, E extends T>(a: T[], b: ArrayLike<E>, x: E, y: E, z: E) {
  for (let i = 0, l = b.length; i < l; i++) {
    a.push(b[i]);
  }
  a.push(x);
  a.push(y);
  a.push(z);
}

/**
 * @deprecated Simply use dst.push(...src) instead
 */
export function pushArray<T, E extends T>(dst: T[] | undefined, src: Array<E>) {
  if (!src || dst === undefined) {
    return;
  }
  dst.push(...src);
}

export function pushArrayReversed<T, E extends T>(dst: T[], src: ArrayLike<E>) {
  if (!src) {
    return;
  }

  for (let i = 0, l = src.length; i < l; i++) {
    dst.push(src[l - i - 1]);
  }
}

export function sumArray(a: number[]): number {
  let s = 0;
  for (let i = 0, l = a.length; i < l; i++) {
    s += a[i];
  }
  return s;
}

export function denormalizeParam(wallLength: number, edgeParam: number, edgeParamNormalized: boolean) {
  return edgeParamNormalized ? wallLength * edgeParam : edgeParam;
}

export function ascendingSortCompare(a: number, b: number): number {
  return a - b;
}

export function descendingSortCompare(a: number, b: number): number {
  return b - a;
}

export function ascendingStringSortCompare(a: string, b: string): number {
  if (a === undefined && b === undefined) {
    return 0;
  }
  if (a === undefined) {
    return -1;
  }
  if (b === undefined) {
    return 1;
  }
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  } else {
    return 0;
  }
}

export function init2DArray<T>(rows: number, cols: number = 0): T[][] {
  const a = new Array<Array<T>>(rows);
  for (let i = 0; i < rows; i++) {
    a[i] = new Array<T>(cols);
  }
  return a;
}

/**
 * Float remainder that returns a positive result even if the first parameter is negative.
 * Second parameter should be set to a positive number.
 */
export function fmod(a: number, b: number): number {
  return a - Math.floor(a / b) * b;
}
